<template>
  <section
    v-if="value && activePoint"
    ref="panel"
    class="vw"
    :class="{ 'vw--dragging': dragActive }"
    :style="panelPositionStyle"
    role="region"
    :aria-labelledby="titleId"
  >
    <main class="vw__main">
      <header
        class="vw__header"
        title="Drag to reposition"
        @pointerdown="startDrag"
      >
        <span class="vw__drag-grip" aria-hidden="true">
          <v-icon size="18">mdi-drag</v-icon>
        </span>
        <span class="vw__point-badge">{{ activePoint.label }}</span>
        <div class="vw__header-copy">
          <h2 :id="titleId">Guided verification</h2>
        </div>
        <span class="vw__stage-label">
          Step {{ currentStage }} <i aria-hidden="true">·</i> {{ currentMeta.title }}
        </span>
        <v-btn
          icon
          small
          class="vw__close"
          aria-label="Close verification wizard"
          @click="close"
        >
          <v-icon size="21">mdi-close</v-icon>
        </v-btn>
      </header>

      <div class="vw__guide" aria-live="polite">
        <div class="vw__guide-item vw__guide-item--summary">
          <span class="vw__guide-status" :class="{ complete: currentStage > 1 }">
            <v-icon size="17">
              {{ currentStage > 1 ? 'mdi-check' : 'mdi-map-marker-outline' }}
            </v-icon>
          </span>
          <span class="vw__guide-copy">
            <strong>{{ guideSummary.title }}</strong>
            <small>{{ guideSummary.detail }}</small>
          </span>
          <v-btn
            v-if="currentStage === 2"
            outlined
            class="vw__guide-secondary"
            :disabled="pickArmed"
            @click="editDesign"
          >
            Edit
          </v-btn>
          <v-btn
            v-else-if="currentStage === 3"
            outlined
            class="vw__guide-secondary"
            @click="remeasure"
          >
            Remeasure
          </v-btn>
        </div>

        <div class="vw__guide-item vw__guide-item--current">
          <span class="vw__guide-status current">{{ currentStage }}</span>
          <span class="vw__guide-copy">
            <strong>{{ guideCurrent.title }}</strong>
            <small>{{ guideCurrent.detail }}</small>
          </span>
          <v-btn
            v-if="currentStage === 2 && !manualMode && !showInitialPlaneChoice"
            icon
            color="primary"
            class="vw__guide-disclosure"
            :disabled="captureGateActive"
            :aria-label="trayExpanded ? 'Hide capture options' : 'Show capture options'"
            @click="trayExpanded = !trayExpanded"
          >
            <v-icon size="18">
              {{ trayExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
            </v-icon>
          </v-btn>
          <v-btn
            v-else-if="currentStage === 3"
            text
            color="primary"
            class="vw__guide-secondary"
            @click="trayExpanded = !trayExpanded"
          >
            {{ trayExpanded ? 'Hide details' : 'Details' }}
          </v-btn>
          <v-btn
            v-if="showGuidePrimary"
            color="primary"
            depressed
            class="vw__guide-primary"
            :disabled="guidePrimaryDisabled"
            @click="runGuidePrimary"
          >
            {{ guidePrimaryLabel }}
            <v-icon right size="17">mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </div>

      <v-expand-transition
        @after-enter="constrainPanelPosition"
        @after-leave="constrainPanelPosition"
      >
        <div v-show="trayExpanded" class="vw__tray">
          <div class="vw__content">
        <!-- Design --------------------------------------------------------- -->
        <section v-if="currentStage === 1" class="vw__pane" aria-label="Design point">
          <div v-if="designPickArmed" class="vw__pick-state" aria-live="polite">
            <div class="vw__pick-icon">
              <v-icon color="primary" size="30">mdi-target</v-icon>
            </div>
            <div>
              <h3>Pick the design point on the model</h3>
              <p>
                The viewer is active. Click the required location and the three
                coordinates will be filled automatically.
              </p>
            </div>
            <v-btn outlined color="primary" @click="cancelDesignPick">
              Cancel model pick
            </v-btn>
          </div>

          <template v-else>
            <v-alert
              v-if="designPickError"
              dense
              text
              type="warning"
              class="vw__alert"
              role="alert"
            >
              {{ designPickError }}
            </v-alert>

            <div class="vw__coordinate-entry">
              <div class="vw__coordinate-fields">
                <div
                  v-for="field in axisFields"
                  :key="`design-${field.key}`"
                  class="vw__coordinate-field"
                >
                  <!-- type="text", NOT type="number", and this is deliberate.
                       With no `step` attribute the browser defaults to step=1,
                       so a coordinate carried to 0.1 mm fights its own control:
                       the spinner walks in whole metres and strict browsers
                       flag the value as invalid. `type="number"` also blanks
                       partial input mid-typing (TolerancePicker.vue:167-169
                       documents the same trap). `inputmode="decimal"` keeps the
                       numeric keypad on mobile. Every sibling coordinate field
                       in this wizard already ships this way.

                       The rule ships WITH the type change and is not optional.
                       `type="number"` was doing the input rejection: the browser
                       refused a comma, a stray letter, a pasted "589.1234 m".
                       Plain text accepts all of it, `toNumber` returns null and
                       `canSaveDesign` goes false — so without a visible message
                       the surveyor gets a Save button that silently will not
                       work and no reason why. `hide-details="auto"` reserves no
                       space until there IS a message, so the three-across grid
                       only grows when something is actually wrong. -->
                  <v-text-field
                    :id="`design-${field.key}`"
                    v-model="designRaw[field.key]"
                    :label="`${field.short} · ${field.name}`"
                    :rules="[requiredNumberRule]"
                    type="text"
                    inputmode="decimal"
                    :suffix="coordinateUnitSuffix"
                    outlined
                    dense
                    hide-details="auto"
                    @input="markDesignManual"
                    @keyup.enter="saveDesign"
                  />
                </div>
              </div>

              <v-btn
                outlined
                color="primary"
                class="vw__pick-action"
                @click="startDesignPick"
              >
                <v-icon size="22">mdi-target</v-icon>
                <span>Pick</span>
              </v-btn>
            </div>

            <!-- The fields above hold MODEL-frame numbers; the result card and
                 the report state the same point in SITE metres. This is the one
                 surface that shows both at once, so a surveyor holding a site
                 coordinate can tell BEFORE saving whether they entered the point
                 they meant. Read-only: enumerated `viewerToSite` consumer 5 in
                 the modelUnits.js header, and nothing converts inbound. -->
            <p class="vw__site-preview">
              <span>{{ designSitePreview.label }}</span>
              <strong>{{ designSitePreview.value }}</strong>
            </p>

            <button
              type="button"
              class="vw__disclosure"
              :aria-expanded="designOffsetExpanded ? 'true' : 'false'"
              aria-controls="design-offset-fields"
              @click="designOffsetExpanded = !designOffsetExpanded"
            >
              <span>
                <strong>Add an optional offset</strong>
                <small>Apply a coordinate correction only when required.</small>
              </span>
              <v-icon size="20">
                {{ designOffsetExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </button>

            <v-expand-transition>
              <div
                v-show="designOffsetExpanded"
                id="design-offset-fields"
                class="vw__offsets"
              >
                <div class="vw__offset-grid">
                  <v-text-field
                    v-for="field in axisFields"
                    :key="`design-offset-${field.key}`"
                    v-model="designOffset[field.key]"
                    :label="`${field.short} offset`"
                    :rules="[optionalNumberRule]"
                    type="text"
                    inputmode="decimal"
                    :suffix="coordinateUnitSuffix"
                    outlined
                    dense
                    hide-details="auto"
                  />
                </div>
                <p class="vw__offset-hint">{{ offsetFrameHint }}</p>
                <div class="vw__adjusted">
                  <span>Adjusted coordinate</span>
                  <strong v-for="field in axisFields" :key="`design-adjusted-${field.key}`">
                    {{ field.short }} {{ designAdjustedText[field.key] }}
                  </strong>
                </div>
              </div>
            </v-expand-transition>
          </template>
        </section>

        <!-- As-built ------------------------------------------------------- -->
        <section
          v-else-if="currentStage === 2"
          class="vw__pane"
          aria-label="As-built point"
        >
          <template v-if="manualMode">
            <div class="vw__section-heading">
              <h3>Enter the as-built coordinates</h3>
              <p>
                Captured axes are carried over. Enter only the coordinates that
                still need a value.
              </p>
            </div>

            <div class="vw__coordinate-fields vw__coordinate-fields--manual">
              <div
                v-for="field in axisFields"
                :key="`manual-${field.key}`"
                class="vw__coordinate-field"
              >
                <v-text-field
                  :id="`manual-${field.key}`"
                  v-model="manualRaw[field.key]"
                  :label="`${field.short} · ${field.name}`"
                  :hint="manualFieldHint(field)"
                  :rules="[requiredNumberRule]"
                  type="text"
                  inputmode="decimal"
                  :suffix="coordinateUnitSuffix"
                  outlined
                  dense
                  hide-details="auto"
                  @keyup.enter="confirmManual"
                />
              </div>
            </div>

            <!-- Same preview, same reason, same single method — see the design
                 step's copy of this block. -->
            <p class="vw__site-preview">
              <span>{{ manualSitePreview.label }}</span>
              <strong>{{ manualSitePreview.value }}</strong>
            </p>

            <button
              type="button"
              class="vw__disclosure"
              :aria-expanded="manualOffsetExpanded ? 'true' : 'false'"
              aria-controls="manual-offset-fields"
              @click="manualOffsetExpanded = !manualOffsetExpanded"
            >
              <span>
                <strong>Add an optional offset</strong>
                <small>Apply a coordinate correction only when required.</small>
              </span>
              <v-icon size="20">
                {{ manualOffsetExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}
              </v-icon>
            </button>

            <v-expand-transition>
              <div
                v-show="manualOffsetExpanded"
                id="manual-offset-fields"
                class="vw__offsets"
              >
                <div class="vw__offset-grid">
                  <v-text-field
                    v-for="field in axisFields"
                    :key="`manual-offset-${field.key}`"
                    v-model="manualOffset[field.key]"
                    :label="`${field.short} offset`"
                    :rules="[optionalNumberRule]"
                    type="text"
                    inputmode="decimal"
                    :suffix="coordinateUnitSuffix"
                    outlined
                    dense
                    hide-details="auto"
                  />
                </div>
                <p class="vw__offset-hint">{{ offsetFrameHint }}</p>
              </div>
            </v-expand-transition>
          </template>

          <template v-else>
            <div v-if="showInitialPlaneChoice" class="vw__slice-guidance">
              <v-icon color="primary" size="24">mdi-arrow-down</v-icon>
              <span>
                <strong>Select a slice view from the viewer bar</strong>
                <small>Choose XY, XZ, or YZ in the View menu below, then pick the feature on the cloud.</small>
              </span>
            </div>

            <div class="vw__capture-summary">
              <span>{{ resolvedAxisCount }} of 3 coordinates captured</span>
              <small v-if="captureHelp">{{ captureHelp }}</small>
            </div>

            <!-- The sentences above NAME the views to switch to; these ARE
                 them, so the surveyor does not have to carry a quoted label
                 down to the View menu and find it. `view-change` is the same
                 event name and payload `ViewerStatusBar` emits, so both routes
                 arrive at the one `WorkspacePanel.onViewChange` handler and the
                 slab gets cut either way. Renders only while the panel is
                 actually asking for a view — see `quickViewPlanes`. -->
            <div v-if="quickViewRows.length" class="vw__quick-views">
              <div
                v-for="row in quickViewRows"
                :key="`quick-${row.plane}`"
                class="vw__quick-view-row"
              >
                <span class="vw__quick-view-plane">{{ row.plane }}</span>
                <v-btn
                  v-for="view in row.views"
                  :key="`quick-${view.id}`"
                  small
                  outlined
                  color="primary"
                  class="vw__quick-view-btn"
                  :disabled="!viewerReady"
                  :title="view.label"
                  :aria-label="view.label"
                  @click="requestView(view.id)"
                >
                  {{ view.side }}
                </v-btn>
              </div>
            </div>

            <div class="vw__axis-list">
              <div
                v-for="row in captureAxisRows"
                :key="`capture-${row.axis}`"
                class="vw__axis-row"
                :class="{
                  'vw__axis-row--complete': row.resolved,
                  'vw__axis-row--needed': row.needed,
                }"
              >
                <div class="vw__axis-name">
                  <strong>{{ row.label }}</strong>
                  <span>{{ row.name }}</span>
                </div>
                <div class="vw__axis-value">
                  <strong>{{ row.value }}</strong>
                  <small>{{ row.from }}</small>
                </div>
                <v-icon
                  v-if="row.resolved"
                  color="primary"
                  size="22"
                  :aria-label="`${row.label} resolved`"
                >
                  mdi-check-circle
                </v-icon>
                <button
                  v-else
                  type="button"
                  class="vw__axis-lock"
                  :disabled="!row.canToggleLock"
                  :title="row.lockTitle"
                  @click="toggleAxisLock(row)"
                >
                  Use design value
                </button>
              </div>
            </div>

            <div v-if="capturedPlanes.length" class="vw__captured-views">
              <div class="vw__captured-header">
                <span>
                  <strong>Captured views</strong>
                  <small>Replace a view without clearing the other measurements.</small>
                </span>
                <button
                  type="button"
                  :disabled="pickArmed || captureGateActive"
                  @click="$emit('restart-capture')"
                >
                  Start over
                </button>
              </div>
              <div class="vw__captured-list" role="list" aria-label="Captured slice views">
                <button
                  v-for="view in capturedPlanes"
                  :key="`captured-${view.plane}`"
                  type="button"
                  class="vw__captured-view"
                  :class="{ 'is-recapturing': recapturePlane === view.plane }"
                  :disabled="pickArmed || captureGateActive"
                  :aria-label="`Recapture ${view.plane} view (${view.axesText})`"
                  @click="$emit('recapture-plane', view.plane)"
                >
                  <v-icon size="17" aria-hidden="true">
                    {{ recapturePlane === view.plane ? 'mdi-crosshairs-gps' : 'mdi-refresh' }}
                  </v-icon>
                  <span>
                    <strong>{{ view.plane }}</strong>
                    <small>{{ view.axesText }}</small>
                  </span>
                  <span class="vw__captured-action">
                    {{ recapturePlane === view.plane ? 'Picking' : 'Replace' }}
                  </span>
                </button>
              </div>
            </div>

            <v-alert
              v-if="lockSummary.hasLocks"
              dense
              text
              type="info"
              class="vw__alert"
            >
              <div class="vw__alert-actions">
                <span>
                  {{ lockSummary.scope }}. {{ lockSummary.measuredCount }}
                </span>
                <v-btn x-small text color="primary" @click="unlockAllAxes">
                  Measure all axes
                </v-btn>
              </div>
            </v-alert>

            <v-alert
              v-if="lockViolations.length"
              dense
              text
              type="error"
              class="vw__alert"
              role="alert"
            >
              <strong>The pick is outside a design-locked axis.</strong>
              <div
                v-for="violation in lockViolations"
                :key="violation.axis"
                class="vw__gate-row"
              >
                <span>
                  {{ violation.axis.toUpperCase() }} differs by
                  {{ Math.round(violation.deltaMm) }} mm.
                </span>
                <v-btn
                  x-small
                  text
                  color="error"
                  @click="unlockAxis(violation.axis)"
                >
                  Unlock and measure
                </v-btn>
              </div>
            </v-alert>

            <v-alert
              v-if="pendingCoarsePick"
              dense
              text
              type="warning"
              class="vw__alert"
              role="alert"
            >
              <strong>This view is too coarse for the applied tolerance.</strong>
              <p>
                {{ coarsePickScaleText }}; the target is
                {{ coarsePickNeededText }} or finer.
              </p>
              <div class="vw__gate-actions">
                <v-btn small outlined color="primary" @click="$emit('discard-coarse')">
                  Zoom in and retry
                </v-btn>
                <v-btn small text color="primary" @click="$emit('measure-anyway')">
                  Record as low confidence
                </v-btn>
              </div>
            </v-alert>

            <v-alert
              v-else-if="conflict"
              dense
              text
              type="error"
              class="vw__alert"
              role="alert"
            >
              <strong>Two slice views disagree.</strong>
              <p v-for="item in conflict.disagreements" :key="item.axis">
                {{ item.axis.toUpperCase() }} differs by
                {{ Math.round(item.deltaMm) }} mm.
              </p>
              <div class="vw__gate-actions">
                <v-btn small outlined color="primary" @click="$emit('resolve-conflict', 'keep')">
                  Keep previous
                </v-btn>
                <v-btn small text color="error" @click="$emit('resolve-conflict', 'replace')">
                  Replace with new pick
                </v-btn>
              </div>
            </v-alert>

            <v-alert
              v-else-if="showCoarseWarning"
              dense
              text
              type="warning"
              class="vw__alert"
            >
              {{ coarseWarning }}
            </v-alert>

            <div v-if="pickArmed" class="vw__capture-active" aria-live="polite">
              <v-icon color="primary" size="24">mdi-crosshairs-gps</v-icon>
              <span>
                <strong>
                  {{ recapturePlane
                    ? `Recapturing ${recapturePlane} view`
                    : `Ready to capture in ${activePlane} view` }}
                </strong>
                <small>Click the matching point on the cloud.</small>
              </span>
            </div>

            <v-btn
              block
              outlined
              color="primary"
              class="vw__wide-action vw__wide-action--secondary"
              :disabled="pickArmed"
              @click="beginManual"
            >
              <v-icon left size="20">mdi-keyboard-outline</v-icon>
              Enter coordinates manually
            </v-btn>
          </template>
        </section>

        <!-- Review --------------------------------------------------------- -->
        <section v-else class="vw__pane" aria-label="Review result">
          <div
            class="vw__verdict"
            :class="`vw__verdict--${reviewStatus.variant}`"
            role="status"
          >
            <v-icon :color="reviewStatus.color" size="30">
              {{ reviewStatus.icon }}
            </v-icon>
            <span>
              <strong>{{ reviewStatus.label }}</strong>
              <small>{{ reviewStatus.detail }}</small>
            </span>
          </div>

          <div class="vw__result-table" role="table" aria-label="Per-axis verification result">
            <div class="vw__result-head" role="row">
              <span role="columnheader">Coordinate</span>
              <span role="columnheader">Deviation</span>
              <span role="columnheader">Tolerance</span>
              <span role="columnheader">Result</span>
            </div>
            <div
              v-for="row in resultRows"
              :key="`result-${row.axis}`"
              class="vw__result-row"
              role="row"
            >
              <strong role="cell">{{ row.axis }} / {{ row.name }}</strong>
              <span role="cell">{{ row.deviation }}</span>
              <span role="cell">{{ row.tolerance }}</span>
              <span role="cell" :class="`vw__result-status--${row.variant}`">
                <v-icon :color="row.color" size="18">{{ row.icon }}</v-icon>
                {{ row.status }}
              </span>
            </div>
          </div>

          <div class="vw__provenance">
            <v-icon size="20">mdi-file-document-check-outline</v-icon>
            <span>
              Design: {{ designSourceLabel }}
              <i aria-hidden="true">·</i>
              As-built: {{ asBuiltSourceLabel }}
            </span>
          </div>
        </section>
          </div>

          <footer class="vw__footer">
        <template v-if="currentStage === 1">
          <v-btn text :disabled="designPickArmed" @click="cancelDesign">
            {{ naturalStage > 1 ? 'Back to as-built' : 'Cancel' }}
          </v-btn>
          <v-spacer />
          <v-btn
            color="primary"
            depressed
            :disabled="!canSaveDesign"
            @click="saveDesign"
          >
            Save and continue
            <v-icon right size="19">mdi-arrow-right</v-icon>
          </v-btn>
        </template>

        <template v-else-if="currentStage === 2">
          <template v-if="manualMode">
            <v-btn text @click="cancelManual">Back to capture</v-btn>
            <v-spacer />
            <v-btn
              color="primary"
              depressed
              :disabled="!canConfirmManual"
              @click="confirmManual"
            >
              Save and review
              <v-icon right size="19">mdi-arrow-right</v-icon>
            </v-btn>
          </template>
          <template v-else>
            <v-btn text :disabled="pickArmed" @click="editDesign">
              Back to design
            </v-btn>
            <v-spacer />
            <span class="vw__footer-hint">
              {{ captureFooterHint }}
            </span>
            <v-btn
              v-if="capturePrimaryActionAvailable"
              color="primary"
              depressed
              :disabled="capturePrimaryDisabled"
              @click="runCapturePrimary"
            >
              <v-icon left size="20">{{ capturePrimaryIcon }}</v-icon>
              {{ capturePrimaryLabel }}
            </v-btn>
          </template>
        </template>

        <template v-else>
          <v-btn outlined color="primary" @click="remeasure">
            Remeasure
          </v-btn>
          <v-spacer />
          <v-btn color="primary" depressed @click="finishPoint">
            Finish point
            <v-icon right size="19">mdi-check</v-icon>
          </v-btn>
        </template>
          </footer>
        </div>
      </v-expand-transition>
    </main>
  </section>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { EventBus } from '@/event-bus';
import {
  POSITION_DECIMALS,
  adjustPoint,
  formatDelta,
  formatMetres,
  positionDecimalsFor,
} from '@/features/Verification/utils/pointMath';
import { assertViewerPoint, viewerToSite } from '@/features/Verification/utils/modelUnits';
import { formatTolerance } from '@/features/Verification/utils/checkStatusDisplay';
import { AS_BUILT_SOURCE } from '@/features/Verification/modules/verification.module';
import {
  LOCKABLE_AXES,
  PLANE_DEPTH_AXIS,
  canLock,
  checkAgainstLocks,
} from '@/features/Verification/utils/axisLock';
import { PLANE_AXES } from '@/features/Verification/utils/asBuiltDraft';
import {
  verificationViewLabel,
  verificationViewsForPlane,
} from '@/features/Verification/utils/verificationContext';
import { log as debugLog, warn as debugWarn } from '@/utils/debugLogger';

const MODEL_PICK_EVENTS = Object.freeze({
  ARM: 'verification:arm-model-pick',
  CANCEL: 'verification:cancel-model-pick',
  RESULT: 'verification:model-pick',
  FAILED: 'verification:model-pick-failed',
});

const SOURCE = Object.freeze({ MANUAL: 'manual', MODEL_PICK: 'modelPick' });
const EM_DASH = '—';
const PANEL_EDGE_GAP = 8;
/**
 * Keep-out band at the foot of the workspace so a dragged panel can never be
 * parked under the viewer chrome: the 34 px ViewerStatusBar plus one
 * PANEL_EDGE_GAP. It was 74 while a second 32 px slice bar was docked above the
 * status bar; that bar was folded into the status bar, so the band shrank with
 * it. FloatingDocumentViewer keeps its own copy — keep them in step.
 */
const WORKSPACE_BOTTOM_CHROME = 42;

/**
 * `site` is the surveyor's letter for the axis, and it appears ONLY on the site
 * preview line — the same E / N / EL vocabulary `reportFormat.OFFSET_AXES` uses,
 * so a coordinate reads the same in the wizard as it will on the report. It is
 * deliberately not `short`: X/Y/Z name a MODEL axis and label the entry fields,
 * which hold model-frame numbers; E/N/EL name a SITE axis and may only label
 * values that have been through `viewerToSite`.
 */
const AXIS_FIELDS = Object.freeze([
  Object.freeze({ key: 'x', short: 'X', site: 'E', name: 'Eastings', delta: 'dX', pass: 'passX' }),
  Object.freeze({ key: 'y', short: 'Y', site: 'N', name: 'Northings', delta: 'dY', pass: 'passY' }),
  Object.freeze({ key: 'z', short: 'Z', site: 'EL', name: 'Elevation', delta: 'dZ', pass: 'passZ' }),
]);

/** The three slice planes, in the order the View menu lists their pairs. */
const CAPTURE_PLANES = Object.freeze(['XY', 'XZ', 'YZ']);

const STEPS = Object.freeze([
  Object.freeze({
    number: 1,
    title: 'Design',
    description: 'Set the required point',
    heading: 'Set the design point',
    subheading: 'Enter the coordinates or pick the point directly on the model.',
  }),
  Object.freeze({
    number: 2,
    title: 'As-built',
    description: 'Capture the measured point',
    heading: 'Capture the as-built point',
    subheading: 'Capture visible axes, then use another slice only for coordinates still missing.',
  }),
  Object.freeze({
    number: 3,
    title: 'Review',
    description: 'Check the result',
    heading: 'Review the verification result',
    subheading: 'Check the measured deviation before finishing this point.',
  }),
]);

function toNumber(value) {
  if (value === null || value === undefined) return null;
  const trimmed = String(value).trim();
  if (!trimmed) return null;
  const parsed = Number(trimmed);
  return Number.isFinite(parsed) ? parsed : null;
}

function toNumberOrZero(value) {
  const trimmed = String(value ?? '').trim();
  return trimmed ? toNumber(trimmed) : 0;
}

function toField(value) {
  return value === null || value === undefined ? '' : String(value);
}

/**
 * What the surveyor sees when a COORDINATE field is filled in for them.
 *
 * PRE-FILLS ONLY. A model pick answers in full float precision — `25190.0078125`
 * on Merchant — and putting that in a field states a ten-thousandth of a
 * millimetre that nothing in the chain can measure. `decimals` comes from the
 * model's own unit, so this is 4 dp on a metre facility and 1 dp on a millimetre
 * one: 0.1 mm either way (`positionDecimalsFor`).
 *
 * THE ROUNDING IS COMMITTED, NOT JUST DISPLAYED. The field is the source of
 * truth on save, so an untouched pre-fill is stored as it reads. That is
 * deliberate — the discarded remainder is below the resolution every surface in
 * the feature reports — but it is a real write, not a formatting nicety.
 *
 * TYPED VALUES ARE NEVER PUT THROUGH THIS. Reformatting mid-edit would fight the
 * cursor and rewrite digits under the surveyor's hands.
 *
 * An unknown scale formats nothing: with no transform there is no resolution to
 * round to, and inventing one is the class of assumption this feature is being
 * cured of.
 */
function toCoordinateField(value, decimals) {
  if (value === null || value === undefined) return '';
  if (decimals === null || !Number.isFinite(value)) return toField(value);
  return value.toFixed(decimals);
}

/**
 * What a viewer unit is called, for the entry fields' suffix.
 *
 * The fields hold VIEWER units, and this wizard hard-coded `m` on every one of
 * them — true on the facilities it was built against, and a plain lie on
 * Merchant, where the surveyor would be typing millimetres into a box labelled
 * metres. Nothing is claimed for a unit not listed or for an unresolved
 * transform: an empty suffix asks no question the reader cannot answer.
 */
const UNIT_SUFFIX_BY_MM_PER_UNIT = Object.freeze({ 1: 'mm', 10: 'cm', 1000: 'm' });

/** Says which frame a typed coordinate belongs to — the one picks come from. */
const MODEL_FRAME_HINT = 'Model frame — same as picks';

/**
 * What the optional offset is FOR, said once for both entry paths.
 *
 * The mistake it exists to prevent: reading "offset" as the model's
 * `globalOffset` and typing that in. It would be double-counted — `viewerToSite`
 * already applies the model offset at every site boundary — and the report would
 * then print a fabricated survey adjustment of tens of thousands of metres
 * beside a position that had been moved by it twice. Naming the field's real job
 * (a correction to THIS reading) and stating that the frame conversion is
 * automatic leaves no gap for that reading to live in.
 */
const OFFSET_FRAME_HINT = 'A survey correction for this point only. '
  + 'Converting between the model frame and site coordinates is automatic.';

/**
 * The site preview's caption. `≈` because the line is rounded for reading, not
 * a second statement of the value.
 *
 * THE OFFSET VARIANT IS NOT DECORATION. The preview shows the ADJUSTED point —
 * what will actually be stored — while the fields above it show the raw entry
 * and the offset that produced the difference sits inside a collapsed
 * disclosure. Without the caption saying so, a preview that disagreed with every
 * visible field would read as a bug in the preview rather than as the offset
 * doing its job.
 */
const SITE_PREVIEW_LABEL = '≈ site';
const SITE_PREVIEW_LABEL_WITH_OFFSET = '≈ site, offset applied';

function hasOffset(offset) {
  return !!offset && AXIS_FIELDS.some((field) => offset[field.key] !== 0);
}

/**
 * Every axis present and numeric.
 *
 * WHY THE SITE PREVIEW CANNOT JUST LET `viewerToSite` DECIDE. That function
 * refuses a non-finite point, but it never sees one: `adjustPoint` sums raw and
 * offset first, and JavaScript reads `null + 0` as 0 — so a BLANK field arrives
 * as a perfectly finite zero and is previewed as a real coordinate. Caught by
 * `.claude/scratch/wizardPreview.assert.js`, where a point with an empty
 * Northings printed `26.9047 N`, a number the surveyor never typed.
 *
 * The step's save gate already refuses an incomplete entry, so this is
 * unreachable today. It is here because the failure mode is a FABRICATED
 * position rather than a missing one, and a fabricated position is the one thing
 * this feature may never show.
 */
function isCompletePoint(point) {
  return !!point && AXIS_FIELDS.every((field) => Number.isFinite(point[field.key]));
}

/**
 * A VIEWER-unit coordinate, at the facility's own resolution, with its unit.
 *
 * WHY NOT THE SHARED `formatMetres`. That formatter is fixed at
 * `POSITION_DECIMALS` (4 dp) and labelled nothing — right for the SITE metres the
 * result card and the report print, wrong twice over for the raw viewer units
 * these readouts show. On Merchant it rendered `25190.0078` — a ten-thousandth
 * of a millimetre, four orders below anything the feature can measure — and the
 * old wrapper then appended a hard-coded " m" to a millimetre. `decimals` comes
 * from the resolved transform via `positionDecimalsFor`, so both facilities land
 * on the same physical 0.1 mm, and `suffix` from `UNIT_SUFFIX_BY_MM_PER_UNIT`, so
 * an unresolved transform claims no unit at all rather than guessing metres.
 *
 * U+2212 MINUS, matching `formatMetres` — these readouts share panels with values
 * that formatter produces, and two minus glyphs in one column read as two
 * different kinds of number.
 */
function formatViewerCoordinate(value, decimals, suffix) {
  if (!Number.isFinite(value)) return EM_DASH;
  const magnitude = Math.abs(value).toFixed(Number.isFinite(decimals) ? decimals : POSITION_DECIMALS);
  const rendered = value < 0 ? `−${magnitude}` : magnitude;
  return suffix ? `${rendered} ${suffix}` : rendered;
}

/**
 * The SITE-metre reading of a model-frame point, as one line:
 * `15.6621 E · 2.7427 N · 40.2853 EL`.
 *
 * WHY THE ENTRY STEP SHOWS SITE METRES AT ALL. Everything the surveyor types
 * here is model frame, and everything the result card and the report print is
 * site metres. On Merchant those are not close: a design point typed as
 * `25190.0078` is recorded at `15.6621` — same location, two frames, and no
 * surface previously showed both at once. A surveyor with a site coordinate in
 * hand had no way to tell, before saving, whether the number they had entered
 * was the point they meant. This line is that check, and nothing more: it is
 * READ-ONLY and NOTHING IS EVER CONVERTED INBOUND. It is enumerated consumer 5
 * in the modelUnits.js header.
 *
 * `formatMetres` and not a local `toFixed`: the result card's position cells go
 * through that same formatter, so the preview and the value it is previewing
 * cannot round differently or print two different minus glyphs.
 *
 * ON A METRE FACILITY WITH NO GLOBAL OFFSET THIS ECHOES THE INPUTS. That is the
 * honest answer, not a broken one — the two frames genuinely coincide there, and
 * seeing them agree is what tells the surveyor the facility is that simple.
 *
 * @param {{x:number,y:number,z:number}|null} point Model-frame, offset already
 *   applied — the point that will actually be stored.
 * @param {object|null} transform `GET_UNIT_TRANSFORM`.
 * @returns {string|null} null when the point or the transform cannot answer,
 *   which the caller shows as '—' rather than as an unconverted number.
 */
function formatSitePreview(point, transform) {
  const site = viewerToSite(point, transform);
  if (!site) return null;
  return AXIS_FIELDS
    .map((field) => `${formatMetres(site[field.key])} ${field.site}`)
    .join(' · ');
}

export default {
  name: 'VerificationWizard',
  props: {
    value: { type: Boolean, default: false },
    activePlane: { type: String, default: '3D' },
    recapturePlane: { type: String, default: null },
    viewerReady: { type: Boolean, default: false },
    pickArmed: { type: Boolean, default: false },
    canUsePoint: { type: Boolean, default: false },
    conflict: { type: Object, default: null },
    pendingCoarsePick: { type: Object, default: null },
    coarsePickScaleText: { type: String, default: '' },
    coarsePickNeededText: { type: String, default: '' },
    showCoarseWarning: { type: Boolean, default: false },
    coarseWarning: { type: String, default: '' },
  },
  data() {
    return {
      stageOverride: null,
      designRaw: { x: '', y: '', z: '' },
      designOffset: { x: '0', y: '0', z: '0' },
      designSource: null,
      designOffsetExpanded: false,
      designPickArmed: false,
      designPickError: null,
      manualMode: false,
      manualRaw: { x: '', y: '', z: '' },
      manualOffset: { x: '0', y: '0', z: '0' },
      manualOffsetExpanded: false,
      manualSeed: { x: null, y: null, z: null },
      manualSeedContributions: [],
      trayExpanded: false,
      dragActive: false,
      dragPosition: null,
      dragPointerId: null,
      dragOffset: { x: 0, y: 0 },
    };
  },
  computed: {
    ...mapGetters('VERIFICATION', {
      activePoint: 'GET_ACTIVE_POINT',
      draftStatus: 'GET_AS_BUILT_DRAFT_STATUS',
      draftRaw: 'GET_AS_BUILT_DRAFT',
      lockSummary: 'GET_LOCK_SUMMARY',
      // Threaded into `checkAgainstLocks` — the lock corridor compares a
      // viewer-space gap against a millimetre tolerance, and that util is pure
      // by design so it cannot look the scale up itself. Also what tells the
      // entry fields how many decimals to pre-fill and what to call their unit.
      unitTransform: 'GET_UNIT_TRANSFORM',
      // Reference geometry for the manual-entry sanity warning. A box is a far
      // stronger reference than the transform — see `assertViewerPoint`.
      verifiedBBox: 'GET_VERIFIED_BBOX',
    }),
    ...mapState('VERIFICATION', ['asBuiltLocks']),
    titleId() {
      return `verification-wizard-title-${this.activePoint?.id || 'point'}`;
    },
    activePointId() {
      return this.activePoint?.id || null;
    },
    panelPositionStyle() {
      if (!this.dragPosition) return null;
      return {
        top: '0',
        right: 'auto',
        left: '0',
        transform: `translate3d(${this.dragPosition.left}px, ${this.dragPosition.top}px, 0)`,
      };
    },
    axisFields() {
      return AXIS_FIELDS;
    },

    /**
     * Decimals for a coordinate PRE-FILL. The fields hold viewer units, so the
     * answer is a property of the facility's model, not a constant: 0.1 mm
     * resolution is 4 dp of a metre and 1 dp of a millimetre. Null until the
     * transform resolves, which `toCoordinateField` reads as "do not round".
     */
    coordinateDecimals() {
      return positionDecimalsFor(this.unitTransform?.mmPerViewerUnit ?? null);
    },

    /** What to call a viewer unit on an entry field. '' when it is not known. */
    coordinateUnitSuffix() {
      return UNIT_SUFFIX_BY_MM_PER_UNIT[this.unitTransform?.mmPerViewerUnit] || '';
    },
    naturalStage() {
      if (!this.activePoint?.design) return 1;
      if (!this.activePoint?.asBuilt) return 2;
      return 3;
    },
    currentStage() {
      return this.stageOverride || this.naturalStage;
    },
    currentMeta() {
      return STEPS[this.currentStage - 1];
    },
    guideSummary() {
      if (this.currentStage === 1) {
        return {
          title: `Point ${this.activePoint?.label || ''}`,
          detail: 'Ready to define the design location',
        };
      }
      if (this.currentStage === 2) {
        return {
          title: 'Design point',
          detail: this.compactPoint(this.activePoint?.design),
        };
      }
      return {
        title: 'As-built point',
        detail: this.asBuiltSourceLabel,
      };
    },
    guideCurrent() {
      if (this.currentStage === 1) {
        return {
          title: 'Set design point',
          detail: 'Enter X/Y/Z or pick on the model',
        };
      }
      if (this.currentStage === 2) {
        return {
          title: 'Capture as-built point',
          detail: this.captureHelp,
        };
      }
      return {
        title: 'Review result',
        detail: this.reviewStatus.label,
      };
    },
    showGuidePrimary() {
      if (this.currentStage === 2) {
        return !this.trayExpanded && this.capturePrimaryActionAvailable;
      }
      return !this.trayExpanded;
    },
    guidePrimaryLabel() {
      if (this.currentStage === 1) return 'Set design';
      if (this.currentStage === 2) return this.capturePrimaryLabel;
      return 'Finish point';
    },
    guidePrimaryDisabled() {
      return this.currentStage === 2 ? this.capturePrimaryDisabled : false;
    },
    captureGateActive() {
      return !!this.pendingCoarsePick
        || !!this.conflict
        || !!this.lockViolations.length;
    },
    parsedDesignRaw() {
      return {
        x: toNumber(this.designRaw.x),
        y: toNumber(this.designRaw.y),
        z: toNumber(this.designRaw.z),
      };
    },
    parsedDesignOffset() {
      return {
        x: toNumberOrZero(this.designOffset.x),
        y: toNumberOrZero(this.designOffset.y),
        z: toNumberOrZero(this.designOffset.z),
      };
    },
    canSaveDesign() {
      return !!this.activePoint
        && !this.designPickArmed
        && AXIS_FIELDS.every((field) => this.parsedDesignRaw[field.key] !== null)
        && AXIS_FIELDS.every((field) => this.parsedDesignOffset[field.key] !== null);
    },
    designAdjustedText() {
      const blank = { x: EM_DASH, y: EM_DASH, z: EM_DASH };
      if (!this.canSaveDesign) return blank;
      const adjusted = adjustPoint(this.parsedDesignRaw, this.parsedDesignOffset);
      // VIEWER units, at the facility's own resolution — the whole point of this
      // readout is to let the surveyor confirm the offset landed, so it has to
      // resolve at least as finely as they can type into the fields above it,
      // and those fields are pre-filled at exactly this precision.
      return {
        x: this.formatCoordinate(adjusted.x),
        y: this.formatCoordinate(adjusted.y),
        z: this.formatCoordinate(adjusted.z),
      };
    },
    designSitePreview() {
      return this.sitePreview(
        this.parsedDesignRaw,
        this.parsedDesignOffset,
        this.canSaveDesign,
      );
    },
    draftForActivePoint() {
      if (!this.activePoint?.id || this.draftStatus?.pointId !== this.activePoint.id) return null;
      return this.draftStatus;
    },
    captureAxisRows() {
      const axes = this.draftForActivePoint?.axes || {};
      const sources = this.draftRaw?.pointId === this.activePoint?.id
        ? (this.draftRaw.axisSource || {})
        : {};
      const design = this.activePoint?.design || null;

      return AXIS_FIELDS.map((field) => {
        const axis = field.key;
        const locked = Number.isFinite(this.asBuiltLocks?.[axis]);
        const measured = Number.isFinite(axes[axis]);
        const verdict = canLock(axis, this.activePlane, design);
        const depthAxis = PLANE_DEPTH_AXIS[this.activePlane] === axis;
        return {
          axis,
          label: field.short,
          name: field.name,
          locked,
          measured,
          resolved: locked || measured,
          needed: !locked && !measured,
          canToggleLock: locked || verdict.allowed,
          lockTitle: verdict.allowed
            ? `Use the design ${field.name.toLowerCase()} instead of measuring it`
            : verdict.reason,
          value: this.formatCoordinate(
            locked ? this.asBuiltLocks[axis] : axes[axis],
            { withUnit: true },
          ),
          from: locked
            ? 'Design value · not measured'
            : (measured
              ? `${sources[axis] || 'Slice'} view`
              : (depthAxis ? 'Needs another view' : 'Not captured yet')),
        };
      });
    },
    resolvedAxisCount() {
      return this.captureAxisRows.filter((row) => row.resolved).length;
    },
    lockViolations() {
      if (!this.lockSummary.hasLocks || !this.draftForActivePoint?.axes) return [];
      return checkAgainstLocks(
        this.draftForActivePoint.axes,
        this.asBuiltLocks,
        this.activePoint?.tolerance,
        this.unitTransform?.mmPerViewerUnit ?? null,
      ).violations;
    },
    captureHelp() {
      if (this.lockViolations.length) return 'Resolve the locked-axis difference to continue.';
      if (this.pendingCoarsePick) return 'The last pick is waiting for a scale decision.';
      if (this.conflict) return 'Choose which slice reading to keep.';
      if (this.pickArmed) {
        return this.recapturePlane
          ? `Click the cloud to replace the ${this.recapturePlane} reading.`
          : `Click the cloud to capture ${this.activePlane}.`;
      }
      const missing = this.captureAxisRows
        .filter((row) => row.needed)
        .map((row) => row.label);
      if (!missing.length) return 'All coordinates are ready to use.';
      if (this.showInitialPlaneChoice) return 'Pick a capture view — XY, XZ or YZ — in the View menu below.';
      if (this.recommendedPlane && this.recommendedPlane !== this.activePlane) {
        return this.planeViewPrompt(this.recommendedPlane);
      }
      return `Still needed: ${missing.join(', ')}.`;
    },
    hasCapturePicks() {
      return !!this.draftForActivePoint?.planes?.length;
    },
    capturedPlanes() {
      if (this.draftRaw?.pointId !== this.activePoint?.id) return [];
      const contributions = this.draftRaw?.contributions || {};
      return ['XY', 'XZ', 'YZ']
        .filter((plane) => !!contributions[plane])
        .map((plane) => ({
          plane,
          axesText: (PLANE_AXES[plane] || [])
            .map((axis) => axis.toUpperCase())
            .join(' + '),
        }));
    },
    showInitialPlaneChoice() {
      return !this.hasCapturePicks && this.activePlane === '3D' && !this.pickArmed;
    },
    recommendedPlane() {
      const missing = this.captureAxisRows.filter((row) => row.needed).map((row) => row.axis);
      if (!this.hasCapturePicks) return null;
      if (missing.includes('z')) return 'XZ';
      if (missing.includes('y')) return 'YZ';
      if (missing.includes('x')) return 'XZ';
      return this.activePlane;
    },

    /**
     * WHICH planes the quick-view buttons offer: ALL of them, for the whole
     * capture step (user request, 2026-08-04 live test). A surveyor flips
     * between opposite faces mid-pick and re-opens a plane after capturing it
     * (the Replace flow), so USING a button must never make the row disappear —
     * the earlier version hid them once a pick armed or the recommended view
     * was active, which stranded the surveyor back in the bottom-bar menu for
     * the second view. Parity note: the bottom-bar View menu stays enabled
     * through all of these states, and the buttons fire its exact event, so
     * persistent buttons cannot reach any state the menu could not.
     *
     * The ONE exception is a blocking gate (`captureGateActive` — units or
     * geometry unresolved): no view can capture anything then, and offering
     * one would contradict the gate's own message.
     */
    quickViewPlanes() {
      if (this.captureGateActive) return [];
      return CAPTURE_PLANES;
    },

    /**
     * The needed planes as button rows, one row per plane, both members of the
     * opposite pair on it.
     *
     * The sentence above says «Pick "Northings · North" or "Northings · South"»;
     * the button faces carry only `side`, because the row already states the
     * plane and the axis name repeated six times is noise. The full label stays
     * on `title`/`aria-label` so the sentence's words are still findable on the
     * control they name — and so a screen reader hears the whole thing.
     *
     * `verificationViewsForPlane` cannot return Isometric (its `sub` is '3D'),
     * so this list structurally cannot offer a view that captures nothing.
     */
    quickViewRows() {
      return this.quickViewPlanes.map((plane) => ({
        plane,
        views: verificationViewsForPlane(plane).map((view) => ({
          id: view.id,
          side: view.side,
          label: verificationViewLabel(view),
        })),
      }));
    },
    offsetFrameHint() {
      return OFFSET_FRAME_HINT;
    },
    capturePrimaryActionAvailable() {
      return this.canUsePoint || this.pickArmed;
    },
    capturePrimaryLabel() {
      if (this.pickArmed) return this.recapturePlane ? 'Cancel recapture' : 'Stop picking';
      if (this.canUsePoint) return 'Use captured point';
      return '';
    },
    capturePrimaryIcon() {
      if (this.pickArmed) return 'mdi-stop-circle-outline';
      if (this.canUsePoint) return 'mdi-check';
      return '';
    },
    capturePrimaryDisabled() {
      if (this.pickArmed) return false;
      return !this.activePoint
        || !!this.pendingCoarsePick
        || !!this.conflict
        || !!this.lockViolations.length
        || (!this.viewerReady && !this.canUsePoint);
    },
    captureFooterHint() {
      if (this.pickArmed) {
        return this.recapturePlane
          ? `Click the cloud to replace ${this.recapturePlane}, or cancel.`
          : 'Click the cloud or stop picking.';
      }
      if (this.canUsePoint) return 'All coordinates are ready.';
      if (this.showInitialPlaneChoice) return 'Pick a capture view — XY, XZ or YZ — in the View menu below.';
      if (this.recommendedPlane && this.recommendedPlane !== this.activePlane) {
        return this.planeViewPrompt(this.recommendedPlane);
      }
      return 'Pick a capture view in the View menu below to continue.';
    },
    parsedManualRaw() {
      return {
        x: toNumber(this.manualRaw.x),
        y: toNumber(this.manualRaw.y),
        z: toNumber(this.manualRaw.z),
      };
    },
    parsedManualOffset() {
      return {
        x: toNumberOrZero(this.manualOffset.x),
        y: toNumberOrZero(this.manualOffset.y),
        z: toNumberOrZero(this.manualOffset.z),
      };
    },
    canConfirmManual() {
      return !!this.activePoint
        && AXIS_FIELDS.every((field) => this.parsedManualRaw[field.key] !== null)
        && AXIS_FIELDS.every((field) => this.parsedManualOffset[field.key] !== null);
    },
    manualSitePreview() {
      return this.sitePreview(
        this.parsedManualRaw,
        this.parsedManualOffset,
        this.canConfirmManual,
      );
    },
    manualAxisFromPick() {
      return AXIS_FIELDS.reduce((flags, field) => {
        flags[field.key] = this.manualSeed[field.key] !== null
          && this.manualRaw[field.key] === this.manualSeed[field.key];
        return flags;
      }, {});
    },
    retainedPickAxes() {
      return AXIS_FIELDS
        .map((field) => field.key)
        .filter((key) => this.manualAxisFromPick[key]);
    },
    reviewStatus() {
      if (this.activePoint?.pass === true) {
        return {
          label: 'Within tolerance',
          detail: 'All measured axes meet the applied tolerance.',
          variant: 'pass',
          color: 'primary',
          icon: 'mdi-check-circle-outline',
        };
      }
      if (this.activePoint?.pass === false) {
        return {
          label: 'Outside tolerance',
          detail: 'One or more measured axes require review.',
          variant: 'fail',
          color: 'error',
          icon: 'mdi-alert-circle-outline',
        };
      }
      return {
        label: 'Verification recorded',
        detail: 'At least one axis was declared rather than measured.',
        variant: 'declared',
        color: 'blue',
        icon: 'mdi-information-outline',
      };
    },
    resultRows() {
      const point = this.activePoint || {};
      const locked = point.lockedAxes || [];
      return AXIS_FIELDS.map((field) => {
        const isLocked = locked.includes(field.key);
        const flag = point[field.pass];
        let status = 'Pending';
        let variant = 'pending';
        let icon = 'mdi-clock-outline';
        let color = 'grey';
        if (isLocked) {
          status = 'Declared';
          variant = 'declared';
          icon = 'mdi-lock-outline';
          color = 'blue';
        } else if (flag === true) {
          status = 'Pass';
          variant = 'pass';
          icon = 'mdi-check-circle';
          color = 'primary';
        } else if (flag === false) {
          status = 'Fail';
          variant = 'fail';
          icon = 'mdi-close-circle';
          color = 'error';
        }
        return {
          axis: field.short,
          name: field.name,
          deviation: isLocked
            ? EM_DASH
            : `${formatDelta(point.deltas?.[field.delta])} mm`,
          tolerance: point.tolerance
            ? formatTolerance(point.tolerance[field.key], 'mm')
            : EM_DASH,
          status,
          variant,
          icon,
          color,
        };
      });
    },
    designSourceLabel() {
      return this.activePoint?.designSource === SOURCE.MODEL_PICK ? 'Model pick' : 'Manual entry';
    },
    asBuiltSourceLabel() {
      if (this.activePoint?.asBuiltSource === AS_BUILT_SOURCE.CLOUD_PICK) {
        const planes = this.activePoint.asBuiltPlanes || [];
        return planes.length ? `${planes.join(' + ')} slice views` : 'Cloud pick';
      }
      return 'Manual entry';
    },
  },
  watch: {
    value(isOpen) {
      if (isOpen) {
        this.prepareForPoint();
        this.trayExpanded = this.currentStage === 1 || this.showInitialPlaneChoice;
        this.constrainPanelPosition();
      }
      else this.cancelActiveTools();
    },
    currentStage() {
      this.trayExpanded = this.currentStage === 1 || this.showInitialPlaneChoice;
      this.constrainPanelPosition();
    },
    captureGateActive(needsAttention) {
      if (needsAttention) this.trayExpanded = true;
    },
    activePointId: {
      handler() {
        this.stageOverride = null;
        this.manualMode = false;
        this.prepareForPoint();
        this.trayExpanded = this.currentStage === 1 || this.showInitialPlaneChoice;
        this.constrainPanelPosition();
      },
    },
  },
  created() {
    EventBus.$on(MODEL_PICK_EVENTS.RESULT, this.onDesignModelPick);
    EventBus.$on(MODEL_PICK_EVENTS.FAILED, this.onDesignModelPickFailed);
  },
  mounted() {
    window.addEventListener('resize', this.constrainPanelPosition);
  },
  beforeDestroy() {
    EventBus.$off(MODEL_PICK_EVENTS.RESULT, this.onDesignModelPick);
    EventBus.$off(MODEL_PICK_EVENTS.FAILED, this.onDesignModelPickFailed);
    window.removeEventListener('resize', this.constrainPanelPosition);
    this.removeDragListeners();
    this.cancelActiveTools();
  },
  methods: {
    /**
     * "Pick "Northings · North" or "Northings · South" in the View menu below
     * (XZ)."
     *
     * Two things this replaces. It said "the Slice bar", which stopped existing
     * when that bar was folded into ViewerStatusBar — the surveyor was being
     * pointed at chrome that is not on screen. And it said "select XZ", which
     * identified exactly one control when there were three views and identifies
     * TWO now that each plane is captured by an opposite pair. Naming both
     * members leaves nothing to guess; the plane still rides along in the
     * parenthesis because that is the token the View menu prints in its `sub`
     * column and the one the rest of this panel talks in.
     */
    planeViewPrompt(plane) {
      const labels = verificationViewsForPlane(plane)
        .map((view) => `"${verificationViewLabel(view)}"`);
      if (!labels.length) return `Select ${plane} in the View menu below.`;
      return `Pick ${labels.join(' or ')} in the View menu below (${plane}).`;
    },

    /**
     * The quick-view buttons' one and only action — the SAME event, name and
     * payload the View menu emits (`ViewerStatusBar.requestView`), landing on
     * the same `WorkspacePanel.onViewChange` handler.
     *
     * NO VIEW LOGIC LIVES HERE, and that is the requirement rather than a
     * preference. `onViewChange` does not just pose a camera: it asks
     * SliceController to cut the plane's slab and only falls back to a bare
     * `applyView` when there is no design point to cut against. A button that
     * called `applyView` itself would look identical and quietly do half the
     * job — the camera would move to a view whose slice was never cut, and the
     * pick that followed would be read off unsliced cloud.
     */
    requestView(viewId) {
      if (!viewId) return;
      this.$emit('view-change', viewId);
    },
    startDrag(event) {
      if (event.button !== 0
        || event.target?.closest?.('button, input, textarea, select, a')) return;
      const panel = this.$refs.panel;
      const workspace = panel?.offsetParent;
      if (!panel || !workspace) return;

      const panelRect = panel.getBoundingClientRect();
      const workspaceRect = workspace.getBoundingClientRect();
      this.dragPosition = {
        left: panelRect.left - workspaceRect.left,
        top: panelRect.top - workspaceRect.top,
      };
      this.dragOffset = {
        x: event.clientX - panelRect.left,
        y: event.clientY - panelRect.top,
      };
      this.dragPointerId = event.pointerId;
      this.dragActive = true;
      event.preventDefault();

      window.addEventListener('pointermove', this.onDragMove, { passive: false });
      window.addEventListener('pointerup', this.stopDrag);
      window.addEventListener('pointercancel', this.stopDrag);
    },
    onDragMove(event) {
      if (!this.dragActive || event.pointerId !== this.dragPointerId) return;
      const panel = this.$refs.panel;
      const workspace = panel?.offsetParent;
      if (!panel || !workspace) {
        this.stopDrag();
        return;
      }

      const workspaceRect = workspace.getBoundingClientRect();
      const maxLeft = Math.max(
        PANEL_EDGE_GAP,
        workspace.clientWidth - panel.offsetWidth - PANEL_EDGE_GAP,
      );
      const maxTop = Math.max(
        PANEL_EDGE_GAP,
        workspace.clientHeight - panel.offsetHeight - WORKSPACE_BOTTOM_CHROME,
      );
      this.dragPosition = {
        left: Math.min(
          Math.max(event.clientX - workspaceRect.left - this.dragOffset.x, PANEL_EDGE_GAP),
          maxLeft,
        ),
        top: Math.min(
          Math.max(event.clientY - workspaceRect.top - this.dragOffset.y, PANEL_EDGE_GAP),
          maxTop,
        ),
      };
      event.preventDefault();
    },
    stopDrag(event = null) {
      if (event && event.pointerId !== this.dragPointerId) return;
      this.dragActive = false;
      this.dragPointerId = null;
      this.removeDragListeners();
    },
    removeDragListeners() {
      window.removeEventListener('pointermove', this.onDragMove);
      window.removeEventListener('pointerup', this.stopDrag);
      window.removeEventListener('pointercancel', this.stopDrag);
    },
    constrainPanelPosition() {
      if (!this.dragPosition) return;
      this.$nextTick(() => {
        const panel = this.$refs.panel;
        const workspace = panel?.offsetParent;
        if (!panel || !workspace) return;
        this.dragPosition = {
          left: Math.min(
            Math.max(this.dragPosition.left, PANEL_EDGE_GAP),
            Math.max(PANEL_EDGE_GAP, workspace.clientWidth - panel.offsetWidth - PANEL_EDGE_GAP),
          ),
          top: Math.min(
            Math.max(this.dragPosition.top, PANEL_EDGE_GAP),
            Math.max(
              PANEL_EDGE_GAP,
              workspace.clientHeight - panel.offsetHeight - WORKSPACE_BOTTOM_CHROME,
            ),
          ),
        };
      });
    },
    /**
     * One coordinate readout, bound to the resolved transform. The unit is
     * opt-in: a row showing a single value wants it spelled out, a run of three
     * axes on one line repeats it into noise.
     */
    formatCoordinate(value, { withUnit = false } = {}) {
      return formatViewerCoordinate(
        value,
        this.coordinateDecimals,
        withUnit ? this.coordinateUnitSuffix : '',
      );
    },

    /**
     * The site preview for one set of entry fields. ONE method for both entry
     * paths on purpose — design and manual are the same three fields with the
     * same offset beneath them, and two copies of this would be free to drift
     * (a manual preview reading the DESIGN offset is the exact bug that shape
     * invites, and no formatter-level test can see it).
     *
     * The point previewed is the ADJUSTED one — raw plus offset, via the same
     * `adjustPoint` call `designAdjustedText` makes — because that is what gets
     * stored and therefore what the result card will print. A preview that
     * ignored the offset would agree with the fields and disagree with the
     * record, which is the wrong one of the two to match.
     *
     * `ready` is the step's own save gate, which already requires all three
     * coordinates AND all three offsets to parse. Anything less shows '—': a
     * half-typed coordinate has no site position, and inventing one from the two
     * axes that do parse would move the preview under the surveyor's hands. The
     * `isCompletePoint` guards restate that independently of the gate — see
     * their docblock for the null-arithmetic reason they cannot be dropped.
     */
    sitePreview(parsedRaw, parsedOffset, ready) {
      if (!ready || !isCompletePoint(parsedRaw) || !isCompletePoint(parsedOffset)) {
        return { label: SITE_PREVIEW_LABEL, value: EM_DASH };
      }
      return {
        label: hasOffset(parsedOffset) ? SITE_PREVIEW_LABEL_WITH_OFFSET : SITE_PREVIEW_LABEL,
        value: formatSitePreview(adjustPoint(parsedRaw, parsedOffset), this.unitTransform)
          || EM_DASH,
      };
    },
    compactPoint(point) {
      if (!point) return 'No coordinate recorded';
      // VIEWER units — this prints `activePoint.design` straight from the store.
      // `formatViewerCoordinate` returns an em dash for a missing axis, which is
      // what the old local helper existed to do.
      const { x, y, z } = point;
      return `X ${this.formatCoordinate(x)} · Y ${this.formatCoordinate(y)} · Z ${this.formatCoordinate(z)}`;
    },
    runGuidePrimary() {
      if (this.currentStage === 1) {
        this.trayExpanded = true;
        return;
      }
      if (this.currentStage === 2) {
        this.runCapturePrimary();
        return;
      }
      this.finishPoint();
    },
    /**
     * WHICH FRAME AM I TYPING IN? The field cannot answer it from its own
     * contents — a coordinate looks the same in every space — and this is the
     * only place in the feature where a human supplies one. An axis carried over
     * from a slice pick says so instead: its provenance already answers the
     * question, and repeating the frame there would bury the more specific fact.
     */
    manualFieldHint(field) {
      return this.manualAxisFromPick[field.key] ? 'From slice pick' : MODEL_FRAME_HINT;
    },
    requiredNumberRule(value) {
      return toNumber(value) !== null || 'Enter a number';
    },
    optionalNumberRule(value) {
      return toNumberOrZero(value) !== null || 'Enter a number';
    },
    prepareForPoint() {
      if (!this.activePoint) return;
      this.$store.commit('VERIFICATION/CLEAR_DESIGN_PICK_PREVIEW');
      this.designRaw = {
        x: toCoordinateField(this.activePoint.design?.x, this.coordinateDecimals),
        y: toCoordinateField(this.activePoint.design?.y, this.coordinateDecimals),
        z: toCoordinateField(this.activePoint.design?.z, this.coordinateDecimals),
      };
      this.designOffset = {
        x: toField(this.activePoint.designOffset?.x ?? 0),
        y: toField(this.activePoint.designOffset?.y ?? 0),
        z: toField(this.activePoint.designOffset?.z ?? 0),
      };
      this.designSource = this.activePoint.designSource === SOURCE.MODEL_PICK
        ? SOURCE.MODEL_PICK
        : null;
      this.designOffsetExpanded = hasOffset(this.activePoint.designOffset);
      this.designPickError = null;
    },
    markDesignManual() {
      if (this.designSource === SOURCE.MODEL_PICK) {
        this.designSource = null;
        this.$store.commit('VERIFICATION/CLEAR_DESIGN_PICK_PREVIEW');
      }
    },
    startDesignPick() {
      this.designPickError = null;
      this.$store.commit('VERIFICATION/CLEAR_DESIGN_PICK_PREVIEW');
      this.designPickArmed = true;
      EventBus.$emit(MODEL_PICK_EVENTS.ARM);
    },
    cancelDesignPick() {
      if (!this.designPickArmed) return;
      this.designPickArmed = false;
      EventBus.$emit(MODEL_PICK_EVENTS.CANCEL);
    },
    onDesignModelPick(point) {
      if (!this.designPickArmed) return;
      this.designPickArmed = false;
      if (!point || !Number.isFinite(point.x) || !Number.isFinite(point.y)
        || !Number.isFinite(point.z)) {
        this.designPickError = 'That point could not be resolved. Pick it again.';
        return;
      }
      // A pick answers in full float precision. `String(point.x)` put
      // `25190.0078125` in the box — see `toCoordinateField`.
      this.designRaw = {
        x: toCoordinateField(point.x, this.coordinateDecimals),
        y: toCoordinateField(point.y, this.coordinateDecimals),
        z: toCoordinateField(point.z, this.coordinateDecimals),
      };
      this.designSource = SOURCE.MODEL_PICK;
      this.designPickError = null;
    },
    onDesignModelPickFailed(payload) {
      if (!this.designPickArmed) return;
      this.designPickArmed = false;
      this.$store.commit('VERIFICATION/CLEAR_DESIGN_PICK_PREVIEW');
      this.designPickError = payload?.reason || 'The model is not ready for picking yet.';
    },
    saveDesign() {
      if (!this.canSaveDesign) return;
      const offset = { ...this.parsedDesignOffset };
      const id = this.activePoint.id;
      this.$store.dispatch('VERIFICATION/setDesignPoint', {
        id,
        point: { ...this.parsedDesignRaw },
        source: this.designSource === SOURCE.MODEL_PICK ? SOURCE.MODEL_PICK : SOURCE.MANUAL,
        offset: hasOffset(offset) ? offset : null,
      });
      this.$store.dispatch('VERIFICATION/clearAsBuiltDraft', id);
      this.$store.commit('VERIFICATION/SET_ACTIVE_STEP', 2);
      this.stageOverride = null;
      debugLog('[VerificationWizard] design saved', id);
    },
    cancelDesign() {
      if (this.naturalStage > 1) {
        this.stageOverride = null;
        this.prepareForPoint();
        return;
      }
      this.close();
    },
    editDesign() {
      this.$emit('stop-capture');
      this.stageOverride = 1;
      this.prepareForPoint();
    },
    toggleAxisLock(row) {
      if (!row.canToggleLock) return;
      this.$store.commit('VERIFICATION/SET_AXIS_LOCK', {
        axis: row.axis,
        locked: !row.locked,
        plane: this.activePlane,
      });
    },
    unlockAxis(axis) {
      this.$store.commit('VERIFICATION/SET_AXIS_LOCK', {
        axis,
        locked: false,
        plane: this.activePlane,
      });
    },
    unlockAllAxes() {
      this.$store.commit('VERIFICATION/CLEAR_AXIS_LOCKS');
    },
    runCapturePrimary() {
      if (this.capturePrimaryDisabled) return;
      if (this.pickArmed) {
        this.$emit('toggle-arm');
        return;
      }
      if (this.canUsePoint) {
        this.$emit('use-point');
      }
    },
    beginManual() {
      this.$emit('stop-capture');
      const point = this.activePoint;
      const draft = this.draftForActivePoint;
      const seed = { x: null, y: null, z: null };
      if (draft) {
        AXIS_FIELDS.forEach((field) => {
          if (draft.filled?.[field.key]) {
            seed[field.key] = toCoordinateField(draft.axes[field.key], this.coordinateDecimals);
          }
        });
      }
      // Seed and field are formatted the SAME way on purpose: `manualAxisFromPick`
      // decides "did this value come from a slice pick?" by comparing the two
      // strings, so a formatting difference would silently drop the provenance.
      this.manualSeed = seed;
      this.manualRaw = AXIS_FIELDS.reduce((raw, field) => {
        raw[field.key] = seed[field.key] !== null
          ? seed[field.key]
          : toCoordinateField(point?.asBuilt?.[field.key], this.coordinateDecimals);
        return raw;
      }, {});
      this.manualOffset = {
        x: toField(point?.asBuiltOffset?.x ?? 0),
        y: toField(point?.asBuiltOffset?.y ?? 0),
        z: toField(point?.asBuiltOffset?.z ?? 0),
      };
      this.manualOffsetExpanded = hasOffset(point?.asBuiltOffset);
      this.manualSeedContributions = draft
        ? Object.values(this.draftRaw?.contributions || {}).map((contribution) => ({
          plane: contribution.plane,
          axes: [...(contribution.axes || [])],
          scanName: contribution.scanName ?? null,
          scanIsManual: !!contribution.scanIsManual,
          mmPerPx: contribution.mmPerPx ?? null,
        }))
        : [];
      this.manualMode = true;
      this.trayExpanded = true;
      this.$emit('manual-mode', true);
    },
    cancelManual() {
      this.manualMode = false;
      this.trayExpanded = false;
      this.$emit('manual-mode', false);
    },
    buildManualProvenance() {
      const none = {
        scanName: null,
        scanNames: [],
        scanIsManual: false,
        mmPerPx: null,
        planes: null,
      };
      if (!this.retainedPickAxes.length || !this.manualSeedContributions.length) return none;
      const owner = {};
      this.manualSeedContributions.forEach((contribution) => {
        contribution.axes.forEach((axis) => {
          owner[axis] = contribution.plane;
        });
      });
      const planes = new Set(
        this.retainedPickAxes.map((axis) => owner[axis]).filter(Boolean),
      );
      const backing = this.manualSeedContributions.filter(
        (contribution) => planes.has(contribution.plane),
      );
      if (!backing.length) return none;

      const scanNames = [];
      let scanIsManual = false;
      let mmPerPx = null;
      backing.forEach((contribution) => {
        if (contribution.scanName && !scanNames.includes(contribution.scanName)) {
          scanNames.push(contribution.scanName);
        }
        if (contribution.scanIsManual) scanIsManual = true;
        if (Number.isFinite(contribution.mmPerPx)
          && (mmPerPx === null || contribution.mmPerPx > mmPerPx)) {
          mmPerPx = contribution.mmPerPx;
        }
      });
      const usedPlanes = backing.map((contribution) => contribution.plane);
      return {
        scanName: scanNames.length === 1 ? scanNames[0] : null,
        scanNames,
        scanIsManual,
        mmPerPx,
        planes: usedPlanes.length ? usedPlanes : null,
      };
    },
    confirmManual() {
      if (!this.canConfirmManual) {
        debugWarn('[VerificationWizard] manual save blocked');
        return;
      }
      const offset = { ...this.parsedManualOffset };
      const point = { ...this.parsedManualRaw };

      // THE ONE HUMAN BOUNDARY. Every other point in this feature comes out of
      // a viewer pick and is in viewer units by construction; this one is
      // whatever the surveyor believed the box wanted. A site-metre value typed
      // on a millimetre facility is about a thousand times off and still looks
      // like a coordinate — which is exactly what this warns about. Dev-only,
      // and it warns rather than blocks: a false positive that refused a real
      // reading would be worse than the mistake it guards.
      //
      // The bbox is the stronger reference; the transform is the fallback and is
      // silent on a facility with no globalOffset (see `assertViewerPoint`).
      assertViewerPoint(point, this.verifiedBBox || this.unitTransform);

      const payload = {
        id: this.activePoint.id,
        point,
        source: AS_BUILT_SOURCE.MANUAL,
        offset: hasOffset(offset) ? offset : null,
        ...this.buildManualProvenance(),
      };
      this.$emit('manual-confirm', payload);
      this.manualMode = false;
      this.$emit('manual-mode', false);
      this.stageOverride = null;
    },
    remeasure() {
      const id = this.activePoint?.id;
      if (!id) return;
      this.$store.dispatch('VERIFICATION/resetAsBuiltPoint', id).then(() => {
        this.stageOverride = null;
        this.$emit('remeasure');
      });
    },
    finishPoint() {
      this.close();
    },
    cancelActiveTools() {
      this.stopDrag();
      this.cancelDesignPick();
      this.$store.commit('VERIFICATION/CLEAR_DESIGN_PICK_PREVIEW');
      if (this.pickArmed) this.$emit('stop-capture');
      if (this.manualMode) this.$emit('manual-mode', false);
      this.manualMode = false;
    },
    close() {
      this.cancelActiveTools();
      this.stageOverride = null;
      this.$emit('input', false);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../styles/verification-tokens';

.vw {
  position: absolute;
  z-index: 7;
  top: 12px;
  right: 12px;
  display: block;
  width: min(700px, calc(100% - 24px));
  max-height: calc(100% - 86px);
  overflow: hidden;
  border: 1px solid var(--ds-border-default);
  border-radius: 10px;
  background: var(--v-bg2-base);
  box-shadow:
    0 12px 32px rgba(0, 0, 0, 0.26),
    0 2px 8px rgba(0, 0, 0, 0.18);
  color: var(--v-text-base);
  font-family: var(--ds-font-family-body);
}

.vw--dragging {
  box-shadow:
    0 18px 42px rgba(0, 0, 0, 0.3),
    0 3px 10px rgba(0, 0, 0, 0.2);
  will-change: transform;
}

.vw__main {
  display: flex;
  min-width: 0;
  min-height: 0;
  flex-direction: column;
  background: var(--v-bg2-base);
}

.vw__header {
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 46px;
  padding: 6px 10px;
  border-bottom: 1px solid var(--ds-border-subtle);
  cursor: grab;
  touch-action: none;
  user-select: none;

  &:active {
    cursor: grabbing;
  }

  .vw__header-copy {
    min-width: 0;
    flex: 1;
  }

  h2 {
    margin: 0;
    color: var(--v-text-base);
    font-family: var(--ds-font-family-heading);
    font-size: 14px;
    font-weight: 600;
    line-height: 1.2;
  }
}

.vw__drag-grip {
  display: grid;
  flex: 0 0 22px;
  width: 22px;
  height: 28px;
  place-items: center;
  color: var(--v-text-base);
  cursor: grab;
  opacity: 0.45;
}

.vw--dragging .vw__drag-grip {
  cursor: grabbing;
  opacity: 0.8;
}

.vw__point-badge {
  display: inline-flex;
  align-items: center;
  height: 24px;
  padding: 0 8px;
  border-radius: 4px;
  background: var(--v-primary-base);
  color: #fff;
  font-family: $vf-font-mono;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  max-width: 160px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.vw__stage-label {
  color: var(--v-text-base);
  font-family: $vf-font-mono;
  font-size: 10px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  opacity: 0.55;

  i {
    margin: 0 4px;
    font-style: normal;
  }
}

.vw__guide {
  display: grid;
  grid-template-columns: minmax(0, 0.78fr) minmax(0, 1.42fr);
  min-height: 70px;
  background: var(--v-bg2-base);
}

.vw__guide-item {
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  padding: 10px 14px;

  & + & {
    border-left: 1px solid var(--ds-border-subtle);
  }

  ::v-deep .v-btn {
    flex: 0 0 auto;
    min-width: 0;
    letter-spacing: 0;
    text-transform: none;
  }
}

.vw__guide-status {
  display: grid;
  flex: 0 0 28px;
  width: 28px;
  height: 28px;
  place-items: center;
  border-radius: 50%;
  background: var(--v-bg3-base);
  color: var(--v-text-base);
  font-family: $vf-font-mono;
  font-size: 12px;
  font-weight: 700;
  opacity: 0.72;

  &.complete {
    background: rgba(3, 98, 76, 0.12);
    color: var(--v-primary-base);
    opacity: 1;
  }

  &.current {
    background: var(--v-primary-base);
    color: #fff;
    opacity: 1;
  }
}

.vw__guide-copy {
  display: flex;
  min-width: 0;
  flex: 1;
  flex-direction: column;

  strong {
    overflow: hidden;
    color: var(--v-text-base);
    font-size: 13px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  small {
    overflow: hidden;
    margin-top: 2px;
    color: var(--v-text-base);
    font-family: $vf-font-mono;
    font-size: 10px;
    opacity: 0.55;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.vw__tray {
  display: flex;
  min-height: 0;
  max-height: calc(100vh - 260px);
  flex-direction: column;
  overflow: hidden;
  border-top: 1px solid var(--ds-border-subtle);
}

.vw__content {
  min-height: 0;
  flex: 1;
  overflow-y: auto;
  padding: 14px 16px;
}

.vw__pane {
  display: grid;
  gap: 12px;
}

.vw__coordinate-entry {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 118px;
  gap: 8px;
  align-items: stretch;
}

.vw__coordinate-fields {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 8px;
}

.vw__coordinate-field {
  min-width: 0;
}

.vw__pick-action {
  min-height: 42px;
  align-self: end;
  border-radius: var(--ds-radius-sm);
  letter-spacing: 0;
  text-transform: none;

  ::v-deep .v-btn__content {
    flex-direction: row;
    gap: 4px;
  }
}

.vw__wide-action {
  min-height: 44px;
  margin-top: var(--ds-space-xs);
  border-radius: var(--ds-radius-sm);
  letter-spacing: 0;
  text-transform: none;
}

.vw__wide-action--secondary {
  margin-top: -4px;
}

.vw__disclosure {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ds-space-md);
  width: 100%;
  min-height: 48px;
  padding: 8px 0;
  border: 0;
  border-top: 1px solid var(--ds-border-subtle);
  border-bottom: 1px solid var(--ds-border-subtle);
  background: transparent;
  color: var(--v-text-base);
  cursor: pointer;
  font-family: inherit;
  text-align: left;

  > span {
    display: flex;
    flex-direction: column;
  }

  strong {
    font-size: 13px;
    font-weight: 600;
  }

  small {
    margin-top: 2px;
    font-size: 12px;
    line-height: 1.4;
    opacity: 0.64;
  }

  &:hover {
    color: var(--v-primary-base);
  }

  &:focus-visible {
    outline: var(--ds-focus-ring-width) solid var(--ds-focus-ring-color);
    outline-offset: var(--ds-focus-ring-offset);
  }
}

.vw__offsets {
  display: grid;
  gap: var(--ds-space-sm);
}

.vw__offset-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: var(--ds-space-sm);
}

.vw__adjusted {
  display: grid;
  grid-template-columns: minmax(120px, 1fr) repeat(3, auto);
  gap: var(--ds-space-sm);
  align-items: center;
  padding: 10px 12px;
  border-radius: var(--ds-radius-sm);
  background: rgba(3, 98, 76, 0.07);
  font-size: 12px;

  span {
    opacity: 0.7;
  }

  strong {
    font-variant-numeric: tabular-nums;
  }
}

/**
 * A CAPTION of the fields above it, not a second value competing with them —
 * no fill, no border, one muted line. Tabular figures because three coordinates
 * on one line only read as a set if their digits align.
 */
.vw__site-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin: 0;
  font-size: 12px;

  span {
    opacity: 0.6;
  }

  strong {
    font-variant-numeric: tabular-nums;
    font-weight: 500;
    opacity: 0.8;
  }
}

.vw__offset-hint {
  margin: 0;
  font-size: 11px;
  line-height: 1.45;
  opacity: 0.62;
}

.vw__pick-state {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: var(--ds-space-md);
  align-items: start;

  h3 {
    margin: 0;
    font-family: var(--ds-font-family-heading);
    font-size: 18px;
    font-weight: 600;
  }

  p {
    margin: 4px 0 0;
    font-size: 14px;
    line-height: 1.55;
    opacity: 0.75;
  }

  .v-btn {
    grid-column: 1 / -1;
    min-height: 44px;
  }
}

.vw__pick-icon {
  display: grid;
  width: 48px;
  height: 48px;
  place-items: center;
  border-radius: var(--ds-radius-md);
  background: rgba(3, 98, 76, 0.1);
}

.vw__section-heading {
  h3 {
    margin: 0;
    font-family: var(--ds-font-family-heading);
    font-size: 17px;
    font-weight: 600;
  }

  p {
    margin: 4px 0 0;
    font-size: 13px;
    line-height: 1.5;
    opacity: 0.7;
  }
}

.vw__slice-guidance {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 52px;
  padding: 9px 11px;
  border: 1px solid rgba(3, 98, 76, 0.24);
  border-radius: var(--ds-radius-md);
  background: rgba(3, 98, 76, 0.055);

  > span {
    display: flex;
    min-width: 0;
    flex-direction: column;
  }

  strong {
    font-size: 13px;
    font-weight: 600;
  }

  small {
    margin-top: 2px;
    font-size: 11px;
    opacity: 0.66;
  }
}

.vw__capture-summary {
  display: flex;
  flex-direction: column;
  gap: 2px;
  color: var(--v-primary-base);

  span {
    font-size: 14px;
    font-weight: 600;
  }

  small {
    color: var(--v-text-base);
    font-size: 12px;
    opacity: 0.68;
  }
}

/**
 * One row per plane, the plane's opposite pair on it. The plane token leads
 * because it is what the caption above and the View menu's `sub` column both
 * say, so a row can be matched to the sentence without reading the buttons.
 */
.vw__quick-views {
  display: grid;
  gap: 6px;
}

.vw__quick-view-row {
  display: flex;
  align-items: center;
  gap: 6px;
}

.vw__quick-view-plane {
  min-width: 26px;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  opacity: 0.6;
}

.vw__quick-view-btn {
  padding: 0 10px;
}

.vw__axis-list {
  display: grid;
  gap: 6px;
}

.vw__axis-row {
  display: grid;
  grid-template-columns: minmax(112px, 0.75fr) minmax(118px, 1fr) auto;
  align-items: center;
  gap: var(--ds-space-sm);
  min-height: 50px;
  padding: 7px 10px;
  border: 1px solid var(--ds-border-default);
  border-radius: var(--ds-radius-md);
  background: var(--v-bg3-base);
}

.vw__axis-row--complete {
  border-color: rgba(3, 98, 76, 0.28);
  background: rgba(3, 98, 76, 0.055);
}

.vw__axis-row--needed {
  border-style: dashed;
}

.vw__axis-name,
.vw__axis-value {
  display: flex;
  min-width: 0;
  flex-direction: column;

  strong {
    font-size: 13px;
    font-weight: 600;
  }

  span,
  small {
    overflow: hidden;
    margin-top: 1px;
    font-size: 11px;
    opacity: 0.65;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.vw__axis-value strong {
  font-variant-numeric: tabular-nums;
}

.vw__axis-lock {
  min-height: 36px;
  padding: 0 10px;
  border: 1px solid var(--ds-border-default);
  border-radius: var(--ds-radius-sm);
  background: transparent;
  color: var(--v-primary-base);
  cursor: pointer;
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;

  &:hover:not(:disabled) {
    border-color: var(--v-primary-base);
    background: rgba(3, 98, 76, 0.06);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.42;
  }

  &:focus-visible {
    outline: var(--ds-focus-ring-width) solid var(--ds-focus-ring-color);
    outline-offset: var(--ds-focus-ring-offset);
  }
}

.vw__captured-views {
  display: grid;
  gap: 7px;
  padding-top: 2px;
}

.vw__captured-header {
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  gap: 12px;

  > span {
    display: flex;
    min-width: 0;
    flex-direction: column;
  }

  strong {
    color: var(--v-text-base);
    font-size: 12px;
    font-weight: 600;
  }

  small {
    margin-top: 1px;
    font-size: 10.5px;
    opacity: 0.62;
  }

  button {
    border: 0;
    background: transparent;
    color: var(--v-primary-base);
    cursor: pointer;
    font-family: inherit;
    font-size: 11px;
    font-weight: 600;

    &:disabled {
      cursor: not-allowed;
      opacity: 0.4;
    }

    &:focus-visible {
      outline: var(--ds-focus-ring-width) solid var(--ds-focus-ring-color);
      outline-offset: var(--ds-focus-ring-offset);
    }
  }
}

.vw__captured-list {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 6px;
}

.vw__captured-view {
  display: grid;
  grid-template-columns: 20px minmax(0, 1fr) auto;
  align-items: center;
  gap: 7px;
  min-height: 42px;
  padding: 5px 8px;
  border: 1px solid var(--ds-border-default);
  border-radius: var(--ds-radius-sm);
  background: var(--v-bg3-base);
  color: var(--v-text-base);
  cursor: pointer;
  font-family: inherit;
  text-align: left;

  > span:not(.vw__captured-action) {
    display: flex;
    min-width: 0;
    flex-direction: column;
  }

  strong {
    font-family: $vf-font-mono;
    font-size: 11px;
  }

  small {
    margin-top: 1px;
    font-size: 9.5px;
    opacity: 0.6;
  }

  &:hover:not(:disabled),
  &.is-recapturing {
    border-color: rgba(3, 98, 76, 0.42);
    background: rgba(3, 98, 76, 0.07);
    color: var(--v-primary-base);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.46;
  }

  &:focus-visible {
    outline: var(--ds-focus-ring-width) solid var(--ds-focus-ring-color);
    outline-offset: var(--ds-focus-ring-offset);
  }
}

.vw__captured-action {
  color: var(--v-primary-base);
  font-size: 9.5px;
  font-weight: 600;
}

.vw__capture-active {
  display: flex;
  align-items: center;
  gap: var(--ds-space-sm);
  padding: 12px;
  border: 1px solid rgba(3, 98, 76, 0.35);
  border-radius: var(--ds-radius-md);
  background: rgba(3, 98, 76, 0.08);

  span {
    display: flex;
    flex-direction: column;
  }

  strong {
    font-size: 13px;
  }

  small {
    margin-top: 1px;
    font-size: 12px;
    opacity: 0.68;
  }
}

.vw__alert {
  margin: 0;
  font-size: 12px;

  p {
    margin: 4px 0 0;
  }
}

.vw__alert-actions,
.vw__gate-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--ds-space-sm);
}

.vw__gate-row {
  margin-top: 4px;
}

.vw__gate-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--ds-space-sm);
  margin-top: var(--ds-space-sm);
}

.vw__verdict {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  min-height: 62px;
  padding: 10px;
  border: 1px solid var(--ds-border-default);
  border-radius: var(--ds-radius-md);

  span {
    display: flex;
    flex-direction: column;
  }

  strong {
    font-family: var(--ds-font-family-heading);
    font-size: 17px;
    font-weight: 600;
  }

  small {
    margin-top: 2px;
    font-size: 12px;
    opacity: 0.7;
  }
}

.vw__verdict--pass {
  border-color: rgba(3, 98, 76, 0.42);
  background: rgba(3, 98, 76, 0.07);
  color: var(--v-primary-base);
}

.vw__verdict--fail {
  border-color: rgba(183, 28, 28, 0.38);
  background: rgba(183, 28, 28, 0.06);
  color: var(--v-error-base);
}

.vw__verdict--declared {
  border-color: rgba(84, 125, 154, 0.4);
  background: rgba(84, 125, 154, 0.07);
}

.vw__result-table {
  display: grid;
}

.vw__result-head,
.vw__result-row {
  display: grid;
  grid-template-columns: minmax(118px, 1.2fr) repeat(3, minmax(70px, 0.8fr));
  gap: var(--ds-space-sm);
  align-items: center;
  min-height: 42px;
  border-bottom: 1px solid var(--ds-border-subtle);
  font-size: 12px;
}

.vw__result-head {
  min-height: 30px;
  opacity: 0.68;
}

.vw__result-row > span:last-child {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-weight: 600;
}

.vw__result-status--pass {
  color: var(--v-primary-base);
}

.vw__result-status--fail {
  color: var(--v-error-base);
}

.vw__result-status--declared {
  color: var(--v-blue-base);
}

.vw__provenance {
  display: flex;
  align-items: center;
  gap: var(--ds-space-sm);
  font-size: 12px;
  opacity: 0.72;

  i {
    margin: 0 4px;
    font-style: normal;
  }
}

.vw__footer {
  display: flex;
  align-items: center;
  gap: var(--ds-space-sm);
  min-height: 56px;
  padding: 8px 14px;
  border-top: 1px solid var(--ds-border-subtle);

  ::v-deep .v-btn {
    min-height: 42px;
    border-radius: var(--ds-radius-sm);
    letter-spacing: 0;
    text-transform: none;
  }
}

.vw__guide-primary,
.vw__guide-secondary {
  min-height: 42px !important;
}

.vw__guide-primary {
  padding-right: 16px !important;
  padding-left: 16px !important;
}

.vw__guide-disclosure {
  width: 42px !important;
  height: 42px !important;
}

.vw__footer-hint {
  font-size: 12px;
  opacity: 0.65;
}

.vw ::v-deep .v-btn {
  letter-spacing: 0;
  text-transform: none;
}

.vw ::v-deep .v-btn:focus-visible {
  outline: var(--ds-focus-ring-width) solid var(--ds-focus-ring-color);
  outline-offset: var(--ds-focus-ring-offset);
}

.vw ::v-deep .v-text-field__slot input {
  font-variant-numeric: tabular-nums;
}

.vw ::v-deep .v-input__slot {
  min-height: 42px !important;
}

.theme--dark .vw__axis-row {
  background: var(--v-bg3-base);
}

.theme--dark .vw__axis-row--complete {
  background: rgba(3, 98, 76, 0.16);
}

@media (max-width: 1450px) {
  .vw {
    width: min(640px, calc(100% - 24px));
  }

  .vw__guide {
    grid-template-columns: minmax(0, 0.7fr) minmax(0, 1.5fr);
  }

  .vw__content,
  .vw__footer {
    padding-right: 12px;
    padding-left: 12px;
  }

  .vw__guide-item {
    padding-right: 10px;
    padding-left: 10px;
  }

  .vw__stage-label {
    display: none;
  }
}

@media (max-width: 900px) {
  .vw__coordinate-entry {
    grid-template-columns: 1fr;
  }

  .vw__pick-action {
    min-height: 44px;

    ::v-deep .v-btn__content {
      flex-direction: row;
    }
  }

}

@media (prefers-reduced-motion: reduce) {
  .vw *,
  .vw *::before,
  .vw *::after {
    transition-duration: 0.01ms !important;
  }
}
</style>
